// Forecast accuracy: score a processed-model forecast against an observation series (a dataset, a
// Postgres historical parameter — any time series). Pure functions; the composed-forecast chart
// supplies the parsed forecast points and the observation records.

/** One forecast timestep: its series values keyed as the forecast JSON / CSV adapter writes them. */
export interface ForecastPoint {
  t: number; // epoch ms
  series: Record<string, unknown>; // "mean", "p10" | "0.10", "control", "value", …
}

export interface ObservationPoint {
  t: number; // epoch ms
  value: number;
}

/** Compare one trace of the forecast (mean, p50, control …) or test the observation against a band. */
export type AccuracyMode = "single" | "band";

export type AccuracyMetricId =
  // single value — per timestep
  | "error"
  | "absError"
  | "sqError"
  | "pctError"
  // single value — summary
  | "mae"
  | "rmse"
  | "bias"
  | "mape"
  | "maxAbsError"
  | "nse"
  | "correlation"
  // band — per timestep
  | "outsideBand"
  // band — summary
  | "coverage"
  | "timeOutside"
  | "meanOutside"
  | "meanWidth"
  | "intervalScore"
  // either mode — summary over every percentile the forecast carries
  | "crps";

export interface AccuracyConfig {
  mode: AccuracyMode;
  /** single mode: which trace — "mean", "p50", "control", "value", "p25" … */
  series?: string;
  /** band mode: lower / upper percentile, e.g. [25, 75] */
  band?: [number, number];
  /** Summary metrics to report. */
  metrics: AccuracyMetricId[];
  /** Per-timestep metric drawn on the right-hand axis (must be a timestep metric of the mode). */
  plotMetric?: AccuracyMetricId;
}

export interface MetricDefinition {
  id: AccuracyMetricId;
  label: string;
  description: string;
  modes: AccuracyMode[];
  /** "timestep" metrics have a value per forecast step and can be plotted. */
  kind: "timestep" | "summary";
  /** "unit" = the forecast's unit; otherwise a fixed unit label. */
  unit: "unit" | "%" | "hours" | "" | "unit²";
}

export const ACCURACY_METRICS: MetricDefinition[] = [
  { id: "error", label: "Error", description: "Forecast minus observation", modes: ["single"], kind: "timestep", unit: "unit" },
  { id: "absError", label: "Absolute error", description: "|forecast − observation|", modes: ["single"], kind: "timestep", unit: "unit" },
  { id: "sqError", label: "Squared error", description: "(forecast − observation)²", modes: ["single"], kind: "timestep", unit: "unit²" },
  { id: "pctError", label: "Percent error", description: "(forecast − observation) / observation", modes: ["single"], kind: "timestep", unit: "%" },
  { id: "mae", label: "MAE", description: "Mean absolute error", modes: ["single"], kind: "summary", unit: "unit" },
  { id: "rmse", label: "RMSE", description: "Root mean square error", modes: ["single"], kind: "summary", unit: "unit" },
  { id: "bias", label: "Bias", description: "Mean error — positive means the forecast runs high", modes: ["single"], kind: "summary", unit: "unit" },
  { id: "mape", label: "MAPE", description: "Mean absolute percent error (observations of 0 skipped)", modes: ["single"], kind: "summary", unit: "%" },
  { id: "maxAbsError", label: "Max abs error", description: "Largest absolute error", modes: ["single"], kind: "summary", unit: "unit" },
  { id: "nse", label: "NSE", description: "Nash–Sutcliffe efficiency — 1 is perfect, 0 is no better than the observed mean", modes: ["single"], kind: "summary", unit: "" },
  { id: "correlation", label: "Correlation (r)", description: "Pearson correlation of forecast and observation", modes: ["single"], kind: "summary", unit: "" },
  { id: "outsideBand", label: "Distance outside band", description: "How far the observation is above (+) or below (−) the band; 0 inside", modes: ["band"], kind: "timestep", unit: "unit" },
  { id: "coverage", label: "Coverage", description: "Share of observations inside the band", modes: ["band"], kind: "summary", unit: "%" },
  { id: "timeOutside", label: "Time outside band", description: "Hours the observation spent outside the band", modes: ["band"], kind: "summary", unit: "hours" },
  { id: "meanOutside", label: "Mean distance outside", description: "Average distance outside the band, over the steps that were outside", modes: ["band"], kind: "summary", unit: "unit" },
  { id: "meanWidth", label: "Mean band width", description: "Average upper − lower — how sharp the band is", modes: ["band"], kind: "summary", unit: "unit" },
  { id: "intervalScore", label: "Interval score", description: "Band width plus a penalty for misses (Winkler) — lower is better", modes: ["band"], kind: "summary", unit: "unit" },
  { id: "crps", label: "CRPS (approx.)", description: "Continuous ranked probability score from the forecast's percentiles — lower is better", modes: ["single", "band"], kind: "summary", unit: "unit" },
];

export const metricsForMode = (mode: AccuracyMode, kind?: MetricDefinition["kind"]) =>
  ACCURACY_METRICS.filter((m) => m.modes.includes(mode) && (!kind || m.kind === kind));

export const DEFAULT_ACCURACY_CONFIG: Record<AccuracyMode, AccuracyConfig> = {
  single: { mode: "single", series: "mean", metrics: ["mae", "rmse", "bias", "mape"], plotMetric: "error" },
  band: { mode: "band", band: [25, 75], metrics: ["coverage", "timeOutside", "meanOutside"], plotMetric: "outsideBand" },
};

// ── Series keys ──────────────────────────────────────────────────────────────────────────────────

/** "p10" | "0.10" | "0.1" -> 10; anything else -> null. */
export const percentileOfSeriesKey = (key: string): number | null => {
  const p = /^p_?(\d{1,3})$/i.exec(key);
  if (p) return Number(p[1]);
  const x = Number(key);
  return key.trim() !== "" && !Number.isNaN(x) && x >= 0 && x <= 1 ? Math.round(x * 100) : null;
};

/** The forecast's series as canonical names ("mean", "p10", "control" …) mapped to values. */
export const canonicalSeries = (series: Record<string, unknown>): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(series)) {
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    const pct = percentileOfSeriesKey(key);
    out[pct !== null ? `p${pct}` : key.toLowerCase()] = value;
  }
  return out;
};

/** The traces and bands a forecast offers, for the configuration pickers. */
export const describeForecastSeries = (points: ForecastPoint[]) => {
  const keys = new Set<string>();
  points.slice(0, 5).forEach((p) => Object.keys(canonicalSeries(p.series)).forEach((k) => keys.add(k)));
  const percentiles = Array.from(keys)
    .map((k) => percentileOfSeriesKey(k))
    .filter((p): p is number => p !== null)
    .sort((a, b) => a - b);
  const traces = Array.from(keys).filter((k) => percentileOfSeriesKey(k) === null).sort();
  // Symmetric pairs, widest first: 10–90, 25–75 …
  const bands: Array<[number, number]> = percentiles
    .filter((p) => p < 50 && percentiles.includes(100 - p))
    .map((p) => [p, 100 - p]);
  return { traces, percentiles, bands };
};

// ── Alignment ────────────────────────────────────────────────────────────────────────────────────

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** Typical forecast step in ms (median gap); 1 h when there are too few points to tell. */
export const forecastStepMs = (points: ForecastPoint[]) =>
  points.length < 2 ? 3_600_000 : median(points.slice(1).map((p, i) => p.t - points[i].t));

/**
 * Pair each forecast timestep with the nearest observation within half a forecast step — any
 * overlap is scored, and steps with no observation nearby are left out.
 */
export const alignToForecast = (forecast: ForecastPoint[], observations: ObservationPoint[]) => {
  const fc = [...forecast].sort((a, b) => a.t - b.t);
  const obs = observations.filter((o) => Number.isFinite(o.value) && Number.isFinite(o.t)).sort((a, b) => a.t - b.t);
  const tolerance = Math.max(forecastStepMs(fc) / 2, 60_000);
  const pairs: Array<{ t: number; observed: number; series: Record<string, number> }> = [];
  let cursor = 0;
  for (const point of fc) {
    while (cursor + 1 < obs.length && obs[cursor + 1].t <= point.t) cursor += 1;
    const candidates = [obs[cursor], obs[cursor + 1]].filter(Boolean);
    const nearest = candidates.reduce<ObservationPoint | null>(
      (best, c) => (!best || Math.abs(c.t - point.t) < Math.abs(best.t - point.t) ? c : best),
      null
    );
    if (!nearest || Math.abs(nearest.t - point.t) > tolerance) continue;
    pairs.push({ t: point.t, observed: nearest.value, series: canonicalSeries(point.series) });
  }
  return pairs;
};

// ── Scoring ──────────────────────────────────────────────────────────────────────────────────────

export interface AccuracyResult {
  /** Paired steps actually scored. */
  matched: number;
  /** Summary metrics that could be computed (null when undefined, e.g. NSE with a flat observation). */
  summary: Partial<Record<AccuracyMetricId, number | null>>;
  /** Per-timestep values of `config.plotMetric`, for the right-hand axis. */
  series: Array<{ t: number; value: number }>;
  /** Why fewer things were scored than expected — shown next to the summary. */
  warnings: string[];
}

const mean = (values: number[]) => (values.length ? values.reduce((s, v) => s + v, 0) / values.length : null);

/** Quantile (pinball) loss averaged over the forecast's percentiles, ×2 — a standard CRPS approximation. */
const crpsAt = (observed: number, series: Record<string, number>) => {
  const qs = Object.entries(series)
    .map(([k, v]) => ({ p: percentileOfSeriesKey(k), v }))
    .filter((q): q is { p: number; v: number } => q.p !== null && q.p > 0 && q.p < 100);
  if (!qs.length) return null;
  const loss = qs.map(({ p, v }) => {
    const tau = p / 100;
    return observed >= v ? tau * (observed - v) : (1 - tau) * (v - observed);
  });
  return (2 * loss.reduce((s, l) => s + l, 0)) / loss.length;
};

export function scoreForecast(
  forecast: ForecastPoint[],
  observations: ObservationPoint[],
  config: AccuracyConfig
): AccuracyResult {
  const pairs = alignToForecast(forecast, observations);
  const warnings: string[] = [];
  const summary: AccuracyResult["summary"] = {};
  const wants = new Set(config.metrics);
  const stepHours = forecastStepMs(forecast) / 3_600_000;

  if (!pairs.length) {
    warnings.push("No observations overlap this forecast issue.");
    return { matched: 0, summary, series: [], warnings };
  }

  if (wants.has("crps")) {
    const crps = pairs.map((p) => crpsAt(p.observed, p.series)).filter((v): v is number => v !== null);
    summary.crps = mean(crps);
    if (!crps.length) warnings.push("CRPS needs percentile columns; this forecast has none.");
  }

  if (config.mode === "single") {
    const key = (config.series ?? "mean").toLowerCase();
    const scored = pairs
      .map((p) => ({ t: p.t, o: p.observed, f: p.series[key] }))
      .filter((p): p is { t: number; o: number; f: number } => typeof p.f === "number");
    if (!scored.length) {
      warnings.push(`This forecast has no "${config.series}" values to compare.`);
      return { matched: 0, summary, series: [], warnings };
    }
    const errors = scored.map((p) => p.f - p.o);
    const abs = errors.map(Math.abs);
    const nonZero = scored.filter((p) => p.o !== 0);
    if (nonZero.length < scored.length && (wants.has("mape") || config.plotMetric === "pctError")) {
      warnings.push(`${scored.length - nonZero.length} observation(s) of 0 skipped for percent error.`);
    }
    const obsMean = mean(scored.map((p) => p.o)) as number;
    const ssObs = scored.reduce((s, p) => s + (p.o - obsMean) ** 2, 0);
    const fcMean = mean(scored.map((p) => p.f)) as number;
    const cov = scored.reduce((s, p) => s + (p.f - fcMean) * (p.o - obsMean), 0);
    const ssFc = scored.reduce((s, p) => s + (p.f - fcMean) ** 2, 0);

    const all: Partial<Record<AccuracyMetricId, number | null>> = {
      mae: mean(abs),
      rmse: Math.sqrt(mean(errors.map((e) => e * e)) as number),
      bias: mean(errors),
      mape: nonZero.length ? (mean(nonZero.map((p) => Math.abs((p.f - p.o) / p.o))) as number) * 100 : null,
      maxAbsError: Math.max(...abs),
      nse: ssObs > 0 ? 1 - errors.reduce((s, e) => s + e * e, 0) / ssObs : null,
      correlation: ssObs > 0 && ssFc > 0 ? cov / Math.sqrt(ssObs * ssFc) : null,
    };
    for (const id of config.metrics) if (id in all) summary[id] = all[id] ?? null;

    const perStep: Partial<Record<AccuracyMetricId, (p: { f: number; o: number }) => number | null>> = {
      error: (p) => p.f - p.o,
      absError: (p) => Math.abs(p.f - p.o),
      sqError: (p) => (p.f - p.o) ** 2,
      pctError: (p) => (p.o !== 0 ? ((p.f - p.o) / p.o) * 100 : null),
    };
    const fn = config.plotMetric ? perStep[config.plotMetric] : undefined;
    const series = fn
      ? scored.map((p) => ({ t: p.t, value: fn(p) })).filter((s): s is { t: number; value: number } => s.value !== null)
      : [];
    return { matched: scored.length, summary, series, warnings };
  }

  // band mode
  const [lo, hi] = config.band ?? [25, 75];
  const scored = pairs
    .map((p) => ({ t: p.t, o: p.observed, lower: p.series[`p${lo}`], upper: p.series[`p${hi}`] }))
    .filter(
      (p): p is { t: number; o: number; lower: number; upper: number } =>
        typeof p.lower === "number" && typeof p.upper === "number"
    );
  if (!scored.length) {
    warnings.push(`This forecast has no p${lo}–p${hi} band to compare.`);
    return { matched: 0, summary, series: [], warnings };
  }
  const outside = scored.map((p) => (p.o > p.upper ? p.o - p.upper : p.o < p.lower ? p.o - p.lower : 0));
  const misses = outside.filter((d) => d !== 0);
  const alpha = Math.max(1 - (hi - lo) / 100, 1e-6);
  const all: Partial<Record<AccuracyMetricId, number | null>> = {
    coverage: ((scored.length - misses.length) / scored.length) * 100,
    timeOutside: misses.length * stepHours,
    meanOutside: misses.length ? (mean(misses.map(Math.abs)) as number) : 0,
    meanWidth: mean(scored.map((p) => p.upper - p.lower)),
    intervalScore: mean(
      scored.map(
        (p) =>
          p.upper - p.lower +
          (2 / alpha) * Math.max(p.lower - p.o, 0) +
          (2 / alpha) * Math.max(p.o - p.upper, 0)
      )
    ),
  };
  for (const id of config.metrics) if (id in all) summary[id] = all[id] ?? null;
  const series = config.plotMetric === "outsideBand" ? scored.map((p, i) => ({ t: p.t, value: outside[i] })) : [];
  return { matched: scored.length, summary, series, warnings };
}

/** "12.3 k-cfs", "87.5 %", "6 hours", "0.82" */
export const formatMetric = (def: MetricDefinition, value: number | null | undefined, unit?: string) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const digits = Math.abs(value) >= 100 ? 0 : Math.abs(value) >= 10 ? 1 : 2;
  const n = value.toFixed(digits);
  if (def.unit === "unit") return unit ? `${n} ${unit}` : n;
  if (def.unit === "unit²") return unit ? `${n} (${unit})²` : n;
  if (def.unit === "%") return `${n} %`;
  if (def.unit === "hours") return `${n} h`;
  return n;
};
